import { useEffect, useRef, useState, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import { createRoot, type Root } from 'react-dom/client';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useDarkMode } from '@/hooks/useDarkMode';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { useMobileDrawer } from '@/hooks/useMobileDrawer';
import { mapStateCache, saveSourceData } from '@/lib/mapStateCache';
import { useFilters } from '@/contexts/FilterContext';
import { useMap } from '@/contexts/MapContext';
import type { Unit } from '@/types';
import 'mapbox-gl/dist/mapbox-gl.css';

// Hooks
import { useMapClustering } from './hooks/useMapClustering';
import { useMapLayers } from './hooks/useMapLayers';
import { useMapEvents } from './hooks/useMapEvents';
import { useMapPopups } from './hooks/useMapPopups';
import { useUrlState, getStoredViewport } from './hooks/useUrlState';

// Components
import { MobileInfoDrawer } from './MobileInfoDrawer';
import { ViewportFilterButton } from './ViewportFilterButton';

// Config
import {
  MAP_STYLES,
  DEFAULT_MAP_CONFIG,
  DEFAULT_MAP_CONFIG_GLOBAL,
  DEFAULT_MAP_CONFIG_NA_UK,
  NA_UK_BOUNDS,
  BOUNDS_PADDING,
  ZOOM_THRESHOLDS,
  isValidLocation,
  calculateWeightedCentroidBounds,
} from './mapConfig';
import { getStyleName, isLayerReady } from './mapStyleUtils';

mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_TOKEN || '';

interface MapViewProps {
  units: Unit[];
  selectedUnit: Unit | null;
  hoveredUnit?: Unit | null;
  onUnitClick?: (unit: Unit | null) => void;
  onUnitHover?: (unit: Unit | null) => void;
  onViewportChange?: (bounds: mapboxgl.LngLatBounds) => void;
  viewportFilterEnabled?: boolean;
  onViewportFilterChange?: (enabled: boolean) => void;
  isSidebarCollapsed?: boolean;
  /** Callback when map finishes loading */
  onMapLoaded?: () => void;
}

const DOTS_SOURCE_ID = 'units-dots'; // Unclustered — small dots at zoom 0-9
const SOURCE_ID = 'units'; // Clustered source at zoom 9+

/**
 * Determines the appropriate default map config based on unit distribution.
 * Returns global view if any units are outside NA/UK bounds, otherwise returns NA/UK view.
 */
function getDefaultMapConfig(units: Unit[]) {
  // Filter to units with valid GPS coordinates (not 0,0, not missing)
  const unitsWithLocation = units.filter(u => isValidLocation(u.location));

  // Check if any units are outside NA/UK bounds
  const hasGlobalUnits = unitsWithLocation.some(unit =>
    !NA_UK_BOUNDS.isWithinNaUk(unit.location.lng, unit.location.lat)
  );

  return hasGlobalUnits ? DEFAULT_MAP_CONFIG_GLOBAL : DEFAULT_MAP_CONFIG_NA_UK;
}

export function MapView({
  units,
  selectedUnit,
  hoveredUnit,
  onUnitClick,
  onUnitHover,
  onViewportChange,
  viewportFilterEnabled = false,
  onViewportFilterChange,
  isSidebarCollapsed = false,
  onMapLoaded,
}: MapViewProps) {
  const { isDarkMode } = useDarkMode();
  const isMobile = useIsMobile();
  const [searchParams] = useSearchParams();
  // Latest search params for one-time, read-only decisions inside effects (e.g. the
  // initial fitBounds in the source-sync effect). Read via a ref so that effect does
  // NOT subscribe to URL changes: `moveend` writes zoom/lng/lat to the URL, and
  // subscribing made the source-sync effect re-run on every pan/zoom — re-pushing the
  // full GeoJSON to Mapbox and re-serializing it to sessionStorage (mobile freeze).
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;
  const navigate = useNavigate();
  const { setMap: setMapContext } = useMap();
  const {
    setSelectedCompanyIds,
    setSelectedGroupIds,
    setSelectedIndustryTypes,
    setSelectedModels,
    selectedCompanyIds,
    selectedGroupIds,
    selectedModels,
    selectedIndustryTypes,
    saveFiltersForRestore,
    restoreSavedFilters,
    hasSavedFilters,
    setShouldExpandAll
  } = useFilters();

  // Map refs
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const popup = useRef<mapboxgl.Popup | null>(null);
  const popupNode = useRef<HTMLDivElement | null>(null);
  const popupRoot = useRef<Root | null>(null);
  
  // Shared timeout ref for coordinated hover behavior between markers and popups
  const sharedHoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // State
  const [mapLoaded, setMapLoaded] = useState(false);
  const [styleLoaded, setStyleLoaded] = useState(0);
  const [sourcesReady, setSourcesReady] = useState(false);
  const [currentZoom, setCurrentZoom] = useState<number>(DEFAULT_MAP_CONFIG.zoom);
  const [mapStyleMode, setMapStyleMode] = useState<'street' | 'satellite' | '3d'>('street');
  const mapStyleModeRef = useRef<'street' | 'satellite' | '3d'>('street');
  const [is3DTimeDay, setIs3DTimeDay] = useState<boolean>(!isDarkMode); // Day/Night for 3D mode - defaults to theme
  const [geolocationError, setGeolocationError] = useState<string | null>(null);
  const [webGLSupported, setWebGLSupported] = useState<boolean | null>(null);
  const [hoveredCluster, setHoveredCluster] = useState<{
    units: Unit[];
    coordinates: [number, number];
  } | null>(null);

  // Filter change version counter - increments when filters change to signal cleanup
  const [filterVersion, setFilterVersion] = useState(0);

  // Refs for initialization tracking
  const initialLoadDone = useRef(false);
  // Track if component is mounted (prevents style events firing after cleanup)
  const isMountedRef = useRef(true);
  // Track if we've notified parent that map is fully ready (tiles rendered, sources loaded)
  const hasNotifiedParentRef = useRef(false);

  // Refs for map controls
  const geolocateControlRef = useRef<mapboxgl.GeolocateControl | null>(null);
  const navigationControlRef = useRef<mapboxgl.NavigationControl | null>(null);
  const fullscreenControlRef = useRef<mapboxgl.FullscreenControl | null>(null);
  const resetControlRef = useRef<mapboxgl.IControl | null>(null);
  const styleControlRef = useRef<mapboxgl.IControl | null>(null);
  const timeSwitcherControlRef = useRef<mapboxgl.IControl | null>(null);

  // Refs for current state (used by event handlers to avoid stale closures)
  const unitsRef = useRef<Unit[]>(units);
  const isMobileRef = useRef<boolean>(isMobile);

  // Track previous filter values to detect changes (not initial render)
  const prevFiltersRef = useRef<{
    companies: string[];
    groups: string[];
    models: string[];
    industries: string[];
  } | null>(null);

  // Clear hover state when filters change (prevents stale hover after filter/zoom)
  useEffect(() => {
    const currentFilters = {
      companies: selectedCompanyIds,
      groups: selectedGroupIds,
      models: selectedModels,
      industries: selectedIndustryTypes,
    };

    // Skip on initial render - only react to changes
    if (prevFiltersRef.current === null) {
      prevFiltersRef.current = currentFilters;
      return;
    }

    // Check if any filter actually changed
    const filtersChanged =
      prevFiltersRef.current.companies.join(',') !== currentFilters.companies.join(',') ||
      prevFiltersRef.current.groups.join(',') !== currentFilters.groups.join(',') ||
      prevFiltersRef.current.models.join(',') !== currentFilters.models.join(',') ||
      prevFiltersRef.current.industries.join(',') !== currentFilters.industries.join(',');

    if (filtersChanged) {
      // Clear hover state to prevent stale closures
      if (onUnitHover) {
        onUnitHover(null);
      }
      // Clear hovered cluster too
      setHoveredCluster(null);

      // Clear any pending hover timeouts
      if (sharedHoverTimeoutRef.current) {
        clearTimeout(sharedHoverTimeoutRef.current);
        sharedHoverTimeoutRef.current = null;
      }

      // Increment filter version to signal useMapEvents to clear its internal refs
      setFilterVersion(v => v + 1);
    }

    prevFiltersRef.current = currentFilters;
  }, [selectedCompanyIds, selectedGroupIds, selectedModels, selectedIndustryTypes, onUnitHover]);

  // === HOOKS ===

  // Mobile drawer for info display (mobile only)
  const mobileDrawer = useMobileDrawer();

  // Clustering configuration (GeoJSON generation)
  const { geojson, clusterConfig } = useMapClustering({
    units,
  });

  // URL state synchronization
  const { syncViewportToUrl, syncClusterToUrl, initialClusterCoords, getShareableUrl } = useUrlState({
    map: map.current,
    mapLoaded,
  });

  // Wrappers to sync cluster state with URL
  const handleClusterHover = useCallback((data: { units: Unit[]; coordinates: [number, number] } | null) => {
    setHoveredCluster(data);
    // If closing popup (data is null), clear URL - but only if there's actually a cluster param
    // This prevents unnecessary URL manipulations that can interfere with other URL state
    if (!data) {
      const currentParams = new URLSearchParams(window.location.search);
      if (currentParams.has('cluster')) {
        syncClusterToUrl(null);
      }
    }
  }, [syncClusterToUrl]);

  const handleClusterClick = useCallback((data: { units: Unit[]; coordinates: [number, number] }) => {
    // Set as hovered (opens popup)
    setHoveredCluster(data);
    // Update URL to reflect selected cluster
    syncClusterToUrl(data.coordinates);
  }, [syncClusterToUrl]);

  // Layer management
  const { loadIcons, updateHoverLayer } = useMapLayers({
    map: map.current,
    mapLoaded,
    sourcesReady,
    styleLoaded, // Track style changes to re-add layers
    dotsSourceId: DOTS_SOURCE_ID,
    sourceId: SOURCE_ID,
  });

  // Event handlers
  useMapEvents({
    map: map.current,
    mapLoaded,
    styleLoaded, // Re-register event handlers when style changes (3D mode, etc.)
    sourceId: SOURCE_ID,
    isMobile,
    units, // Filtered units for zoom-out snap
    onUnitClick,
    onUnitHover,
    onClusterHover: handleClusterHover,
    onClusterClick: handleClusterClick,
    hoverLeaveTimeoutRef: sharedHoverTimeoutRef,
    mobileDrawer, // Pass drawer for mobile info display
    filterVersion, // Signal filter changes to clear internal hover refs
  });

  // Popup management
  useMapPopups({
    map: map.current,
    mapLoaded,
    popup: popup.current,
    popupRoot: popupRoot.current,
    popupNode: popupNode.current,
    hoveredUnit: hoveredUnit ?? null,
    hoveredCluster,
    selectedUnit,
    allUnits: units,
    isMobile,
    onUnitClick,
    onUnitHover,
    onClusterHover: handleClusterHover,
    hoverLeaveTimeoutRef: sharedHoverTimeoutRef,
    navigate,
    filterActions: {
      setSelectedCompanyIds,
      setSelectedGroupIds,
      setSelectedIndustryTypes,
      setSelectedModels,
      saveFiltersForRestore,
      restoreSavedFilters,
      setShouldExpandAll,
    },
    hasSavedFilters,
    getShareableUrl,
    isClusterSticky: !!searchParams.get('cluster'), // Stick popup if URL param is present
  });

  // === MAP INITIALIZATION ===

  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    // Reset mounted flag for React 18 StrictMode double-mount cycle.
    // StrictMode runs: mount → cleanup (sets false) → remount, so we must
    // restore it here to allow style-change guards to proceed.
    isMountedRef.current = true;

    // Check for WebGL support first
    if (!mapboxgl.supported()) {
      console.error('❌ [MapView] WebGL is not supported in this browser/environment');
      setWebGLSupported(false);
      return;
    }
    setWebGLSupported(true);

    // CACHE: Check for existing map state (HMR recovery)
    const cachedState = mapStateCache.restoreLayerState();
    if (cachedState) {
      console.log('🔄 [MapView] Detected HMR reload, cached state available for fast restoration');
    }

    const mapInstance = new mapboxgl.Map({
      container: mapContainer.current,
      style: isDarkMode ? MAP_STYLES.dark : MAP_STYLES.light,
      center: DEFAULT_MAP_CONFIG.center,
      zoom: DEFAULT_MAP_CONFIG.zoom,
      maxZoom: DEFAULT_MAP_CONFIG.maxZoom,
      projection: DEFAULT_MAP_CONFIG.projection,
      attributionControl: false, // Hide default attribution
      logoPosition: 'bottom-left', // Logo in bottom-left corner
    });

    // CRITICAL: Assign immediately to prevent race conditions/duplicate initialization
      map.current = mapInstance;

    // Expose map instance to context for sidebar access
    setMapContext(mapInstance);

    // Handle resize events to prevent blank canvas
    const resizeObserver = new ResizeObserver(() => {
      if (map.current) {
        map.current.resize();
      }
    });
    resizeObserver.observe(mapContainer.current);

    mapInstance.on('load', () => {
      // Guard: If map was cleaned up, don't proceed
      if (!map.current) return;

      setMapLoaded(true);
      // NOTE: onMapLoaded is NOT called here. The 'load' event only means the canvas
      // is initialized, but tiles/sources/layers aren't ready yet. The parent is notified
      // via the idle-based effect below once everything is visually rendered.
      setCurrentZoom(mapInstance.getZoom());
      
      // CACHE: If we have cached source data, restore it immediately to prevent flicker
      if (cachedState?.sourceData) {
        console.log('✨ [MapView] Restoring cached source data for instant markers');
        // Note: Sources will be created in the DATA SOURCES effect
        // This just signals that we have data ready to go
      }

      // Inject custom attribution strip under the logo
      const bottomLeft = mapInstance.getContainer().querySelector('.mapboxgl-ctrl-bottom-left');
      if (bottomLeft) {
        const strip = document.createElement('div');
        strip.className = 'custom-map-attribution';
        strip.textContent = '© Mapbox © OpenStreetMap';
        bottomLeft.appendChild(strip);
      }
    });

    // Track zoom changes (state only - URL updated via moveend)
    mapInstance.on('zoom', () => {
      const zoom = mapInstance.getZoom();
      setCurrentZoom(zoom);
    });

    // Create fullscreen control instance
    const fullscreenControl = new mapboxgl.FullscreenControl();
    fullscreenControlRef.current = fullscreenControl;

    // Add geolocate control
    const geolocateControl = new mapboxgl.GeolocateControl({
      positionOptions: { enableHighAccuracy: false },
      trackUserLocation: false,
      showUserHeading: true,
    });

    geolocateControl.on('error', (e) => {
      console.error('Geolocation error:', e);
      // mapbox-gl 3.17 emits a GeolocationPositionError (code/message); there is
      // no wrapped `.error` sub-object in the contract.
      const errorCode = e?.code;
      const errorMessage = e?.message;

      let userMessage = 'Unable to determine your location.';
      if (errorCode !== undefined) {
        switch (errorCode) {
          case 1:
            userMessage = 'Location access denied. Please enable location permissions in your browser settings.';
            break;
          case 2:
            userMessage = 'Location information is unavailable. Please check your device settings.';
            break;
          case 3:
            userMessage = 'Location request timed out. Please try again.';
            break;
          default:
            userMessage = errorMessage ? `Location error: ${errorMessage}` : 'Unknown location error occurred.';
        }
      } else if (errorMessage) {
        userMessage = `Location error: ${errorMessage}`;
      }

      setGeolocationError(userMessage);
      setTimeout(() => setGeolocationError(null), 5000);
    });

    geolocateControl.on('geolocate', () => setGeolocationError(null));

    geolocateControlRef.current = geolocateControl;

    // Create navigation control instance
    const navigationControl = new mapboxgl.NavigationControl({ showCompass: false });
    navigationControlRef.current = navigationControl;



    // Add reset view control (bottom-right, stacked above style switcher)
    class ResetViewControl implements mapboxgl.IControl {
      private _container?: HTMLDivElement;
      private _button?: HTMLButtonElement;
      private _handleClick?: () => void;

      onAdd(map: mapboxgl.Map): HTMLElement {
        this._container = document.createElement('div');
        this._container.className = 'mapboxgl-ctrl mapboxgl-ctrl-group reset-view-control';

        this._button = document.createElement('button');
        this._button.className = 'mapboxgl-ctrl-icon reset-view-button';
        this._button.type = 'button';
        this._button.setAttribute('aria-label', 'Reset map view');
        this._button.title = 'Reset view';

        this._button.innerHTML = `
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/>
            <path d="M21 3v5h-5"/>
            <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/>
            <path d="M3 21v-5h5"/>
          </svg>
        `;

        this._handleClick = () => {
          // Use current units data from ref (not stale closure)
          const currentUnits = unitsRef.current;
          const currentIsMobile = isMobileRef.current;

          if (currentUnits && currentUnits.length > 0) {
            // Filter out units without valid GPS coordinates (not 0,0, not missing)
            const unitsWithRealLocation = currentUnits.filter(t => isValidLocation(t.location));

            if (unitsWithRealLocation.length > 0) {
              // Use weighted centroid bounds to center on where most units are
              // This prevents outliers (e.g., a few units in Europe) from distorting the view
              const coordinates = unitsWithRealLocation.map(u => ({ lat: u.location.lat, lng: u.location.lng }));
              const weightedBounds = calculateWeightedCentroidBounds(coordinates, 0.95);

              if (weightedBounds) {
                const paddingOptions = currentIsMobile ? BOUNDS_PADDING.mobile : BOUNDS_PADDING.desktop;
                const symmetricBounds = new mapboxgl.LngLatBounds(
                  weightedBounds.sw,
                  weightedBounds.ne
                );

                map.fitBounds(symmetricBounds, {
                  padding: paddingOptions,
                  duration: 800,
                  maxZoom: ZOOM_THRESHOLDS.MAX_AUTO_ZOOM,
                  essential: true,
                });
              } else {
                // Fallback to default view
                const defaultConfig = getDefaultMapConfig(currentUnits);
                map.flyTo({
                  center: defaultConfig.center,
                  zoom: defaultConfig.zoom,
                  essential: true,
                });
              }
            } else {
              // No valid locations - use appropriate default view based on fleet distribution
              const defaultConfig = getDefaultMapConfig(currentUnits);
              map.flyTo({
                center: defaultConfig.center,
                zoom: defaultConfig.zoom,
                essential: true,
              });
            }
          } else {
            // No units - fallback to NA/UK default
            map.flyTo({
              center: DEFAULT_MAP_CONFIG_NA_UK.center,
              zoom: DEFAULT_MAP_CONFIG_NA_UK.zoom,
              essential: true,
            });
          }
        };
        this._button.addEventListener('click', this._handleClick);

        this._container.appendChild(this._button);
        return this._container;
      }

      onRemove(): void {
        if (this._button && this._handleClick) {
          this._button.removeEventListener('click', this._handleClick);
        }
        if (this._container && this._container.parentNode) {
          this._container.parentNode.removeChild(this._container);
        }
        this._handleClick = undefined;
        this._button = undefined;
        this._container = undefined;
      }
    }

    const resetControl = new ResetViewControl();
    resetControlRef.current = resetControl;

    // Add style switcher control (top-right, stacked with other controls)
    class StyleSwitcherControl implements mapboxgl.IControl {
      private _container?: HTMLDivElement;
      private _button?: HTMLButtonElement;
      private _handleClick?: () => void;
      private _currentMode: 'street' | 'satellite' | '3d' = 'street';

      onAdd(_map: mapboxgl.Map): HTMLElement {
        this._container = document.createElement('div');
        this._container.className = 'mapboxgl-ctrl mapboxgl-ctrl-group style-switcher-control';

        this._button = document.createElement('button');
        this._button.className = 'mapboxgl-ctrl-icon style-switcher-button';
        this._button.type = 'button';
        this._button.setAttribute('aria-label', 'Toggle map style');
        this._button.title = 'Satellite view'; // Default next: Satellite

        this._updateIcon();

        this._handleClick = () => {
          let nextMode: 'street' | 'satellite' | '3d';

          // Cycle: Street -> Satellite -> 3D -> Street
          if (this._currentMode === 'street') {
            nextMode = 'satellite';
            this._button!.title = '3D view';
          } else if (this._currentMode === 'satellite') {
            nextMode = '3d';
            this._button!.title = 'Street view';
          } else {
            nextMode = 'street';
            this._button!.title = 'Satellite view';
          }
          
          this._currentMode = nextMode;
          setMapStyleMode(nextMode);
          this._updateIcon();
        };
        
        this._button.addEventListener('click', this._handleClick);
        this._container.appendChild(this._button);
        return this._container;
      }

      private _updateIcon() {
        if (!this._button) return;

        // ICONS for the *NEXT* mode (what clicking will do)
        
        // 1. Satellite Icon (Layers stack) - Shown when in Street mode
        const satelliteIcon = `
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polygon points="12 2 2 7 12 12 22 7 12 2"></polygon>
            <polyline points="2 17 12 22 22 17"></polyline>
            <polyline points="2 12 12 17 22 12"></polyline>
          </svg>
        `;

        // 2. 3D Icon (Cube) - Shown when in Satellite mode
        const icon3d = `
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
            <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
            <line x1="12" y1="22.08" x2="12" y2="12"></line>
          </svg>
        `;

        // 3. Street Icon (Map) - Shown when in 3D mode
        const streetIcon = `
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"></polygon>
            <line x1="8" y1="2" x2="8" y2="18"></line>
            <line x1="16" y1="6" x2="16" y2="22"></line>
          </svg>
        `;

        if (this._currentMode === 'street') {
          this._button.innerHTML = satelliteIcon;
        } else if (this._currentMode === 'satellite') {
          this._button.innerHTML = icon3d;
        } else {
          this._button.innerHTML = streetIcon;
        }
      }

      onRemove(): void {
        if (this._button && this._handleClick) {
          this._button.removeEventListener('click', this._handleClick);
        }
        if (this._container && this._container.parentNode) {
          this._container.parentNode.removeChild(this._container);
        }
        this._handleClick = undefined;
        this._button = undefined;
        this._container = undefined;
      }
    }

    const styleControl = new StyleSwitcherControl();
    styleControlRef.current = styleControl;

    // Add time switcher control (3D mode only - Day/Night toggle)
    class TimeSwitcherControl implements mapboxgl.IControl {
      private _container?: HTMLDivElement;
      private _button?: HTMLButtonElement;
      private _handleClick?: () => void;
      private _isDay: boolean;

      constructor(initialIsDay: boolean = true) {
        this._isDay = initialIsDay;
      }

      onAdd(map: mapboxgl.Map): HTMLElement {
        this._container = document.createElement('div');
        this._container.className = 'mapboxgl-ctrl mapboxgl-ctrl-group time-switcher-control';

        this._button = document.createElement('button');
        this._button.className = 'mapboxgl-ctrl-icon time-switcher-button';
        this._button.type = 'button';
        this._button.setAttribute('aria-label', 'Toggle day/night');
        this._button.title = 'Night view';
        
        // Add inline styles to ensure icon centering
        this._button.style.display = 'flex';
        this._button.style.alignItems = 'center';
        this._button.style.justifyContent = 'center';
        this._button.style.padding = '0';

        this._updateIcon();

        this._handleClick = () => {
          this._isDay = !this._isDay;
          setIs3DTimeDay(this._isDay);
          this._button!.title = this._isDay ? 'Night view' : 'Day view';
          this._updateIcon();

          // Apply the lighting preset immediately with proper retry logic
          const applyLightingPreset = () => {
            if (!map) return;
            
            // Wait for style to be loaded
            const tryApplyConfig = (retryCount = 0) => {
              if (!map) return;
              
              if (map.isStyleLoaded()) {
                try {
                  // Apply the Mapbox Standard style lighting preset
                  map.setConfig('basemap', {
                    lightPreset: this._isDay ? 'day' : 'night',
                    showPointOfInterestLabels: false,
                  });
                  console.log(`✅ Applied ${this._isDay ? 'day' : 'night'} lighting preset`);
                } catch (e) {
                  console.warn('Failed to update lighting preset:', e);
                  
                  // Retry after a short delay
                  if (retryCount < 5) {
                    setTimeout(() => tryApplyConfig(retryCount + 1), 200);
                  }
                }
              } else {
                // Style not loaded yet, wait and retry
                console.log('⏳ Waiting for style to load before applying lighting preset...');
                if (retryCount < 10) {
                  setTimeout(() => tryApplyConfig(retryCount + 1), 100);
                }
              }
            };
            
            tryApplyConfig();
          };
          
          applyLightingPreset();
        };

        this._button.addEventListener('click', this._handleClick);
        this._container.appendChild(this._button);
        return this._container;
      }

      private _updateIcon() {
        if (!this._button) return;

        // Sun icon for day mode (clicking switches to night)
        const sunIcon = `
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="5"></circle>
            <line x1="12" y1="1" x2="12" y2="3"></line>
            <line x1="12" y1="21" x2="12" y2="23"></line>
            <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
            <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
            <line x1="1" y1="12" x2="3" y2="12"></line>
            <line x1="21" y1="12" x2="23" y2="12"></line>
            <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
            <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
          </svg>
        `;

        // Moon icon for night mode (clicking switches to day)
        const moonIcon = `
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
          </svg>
        `;

        // Show icon for current state (not what clicking will do)
        this._button.innerHTML = this._isDay ? sunIcon : moonIcon;
      }

      onRemove(): void {
        if (this._button && this._handleClick) {
          this._button.removeEventListener('click', this._handleClick);
        }
        if (this._container && this._container.parentNode) {
          this._container.parentNode.removeChild(this._container);
        }
        this._handleClick = undefined;
        this._button = undefined;
        this._container = undefined;
      }
    }

    const timeSwitcherControl = new TimeSwitcherControl(!isDarkMode);
    timeSwitcherControlRef.current = timeSwitcherControl;

    // Create popup instance and React root
    popupNode.current = document.createElement('div');
    popupRoot.current = createRoot(popupNode.current);
    popup.current = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false,
      maxWidth: 'none',
    });

    return () => {
      setMapLoaded(false);
      setGeolocationError(null);
      initialLoadDone.current = false;
      hasNotifiedParentRef.current = false;
      resizeObserver.disconnect();

      if (popupRoot.current) {
        const rootToUnmount = popupRoot.current;
        popupRoot.current = null;
        queueMicrotask(() => rootToUnmount.unmount());
      }

      // Mark as unmounted FIRST to prevent style events from firing
      isMountedRef.current = false;

      popup.current?.remove();
      popup.current = null;
      geolocateControlRef.current = null;
      navigationControlRef.current = null;
      fullscreenControlRef.current = null;
      resetControlRef.current = null;
      styleControlRef.current = null;
      timeSwitcherControlRef.current = null;
      mapInstance?.remove();
      map.current = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // === RESPONSIVE CONTROL POSITIONING ===
  // Re-position controls when viewport size changes (mobile <-> desktop)
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    const mapInstance = map.current;

    // Remove all responsive controls if they're already added
    if (geolocateControlRef.current) {
      try {
        mapInstance.removeControl(geolocateControlRef.current);
      } catch (_e) {
        // Control wasn't added yet, ignore
      }
    }
    if (navigationControlRef.current) {
      try {
        mapInstance.removeControl(navigationControlRef.current);
      } catch (_e) {
        // Control wasn't added yet, ignore
      }
    }
    if (fullscreenControlRef.current) {
      try {
        mapInstance.removeControl(fullscreenControlRef.current);
      } catch (_e) {
        // Control wasn't added yet, ignore
      }
    }
    if (resetControlRef.current) {
      try {
        mapInstance.removeControl(resetControlRef.current);
      } catch (_e) {
        // Control wasn't added yet, ignore
      }
    }
    if (styleControlRef.current) {
      try {
        mapInstance.removeControl(styleControlRef.current);
      } catch (_e) {
        // Control wasn't added yet, ignore
      }
    }

    // Re-add controls with correct positions based on current viewport
    // Order matters for stacking: first added = top of stack in the corner container

    if (isMobile) {
      // MOBILE LAYOUT: All controls on bottom-right, specific order

      // 1. Navigation (Zoom/Compass) - Top
      if (navigationControlRef.current) {
        mapInstance.addControl(navigationControlRef.current, 'bottom-right');
      }

      // 2. Geolocate (Current Location) - Above Reset
      if (geolocateControlRef.current) {
        mapInstance.addControl(geolocateControlRef.current, 'bottom-right');
      }

      // 3. Style Switcher (Satellite) - Above Reset
      if (styleControlRef.current) {
        mapInstance.addControl(styleControlRef.current, 'bottom-right');
      }

      // 4. Reset View - Bottom
      if (resetControlRef.current) {
        mapInstance.addControl(resetControlRef.current, 'bottom-right');
      }
    } else {
      // DESKTOP LAYOUT: Split controls left/right

      // Bottom-Right Stack
      if (navigationControlRef.current) {
        // Navigation (zoom): Top of bottom-right stack
        mapInstance.addControl(
          navigationControlRef.current,
          'bottom-right'
        );
      }

      if (geolocateControlRef.current) {
        // Geolocate: Bottom of bottom-right stack
        mapInstance.addControl(geolocateControlRef.current, 'bottom-right');
      }

      // Bottom-Left Stack
      if (fullscreenControlRef.current) {
        // Fullscreen: Top of bottom-left stack
        mapInstance.addControl(
          fullscreenControlRef.current,
          'bottom-left'
        );
      }

      if (resetControlRef.current) {
        // Reset: Middle of bottom-left stack (below Fullscreen)
        mapInstance.addControl(
          resetControlRef.current,
          'bottom-left'
        );
      }

      if (styleControlRef.current) {
        // Style switcher: Bottom of bottom-left stack (aligned with Geolocate)
        mapInstance.addControl(
          styleControlRef.current,
          'bottom-left'
        );
      }
    }
  }, [mapLoaded, isMobile]);

  // === UPDATE STATE REFS ===
  // Keep refs in sync with current props/state for event handlers
  useEffect(() => {
    unitsRef.current = units;
    isMobileRef.current = isMobile;
    mapStyleModeRef.current = mapStyleMode;
  }, [units, isMobile, mapStyleMode]);

  // === LOG INITIAL STATE ===
  useEffect(() => {
    console.log(`🎬 [Init] Component mounted with isDarkMode=${isDarkMode}, is3DTimeDay initialized to ${is3DTimeDay}`);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // === SYNC 3D MODE WITH THEME ===
  // Update 3D mode day/night to match theme (dark mode = night, light mode = day)
  // This runs on mount AND whenever theme changes, ensuring 3D lighting stays synced
  useEffect(() => {
    const newValue = !isDarkMode;
    console.log(`🔄 [Theme Sync] isDarkMode=${isDarkMode}, current is3DTimeDay=${is3DTimeDay}, setting to ${newValue}`);
    setIs3DTimeDay(newValue);
  }, [isDarkMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // === 2D MAP STYLE MANAGEMENT ===
  // Handle 2D map style switching (street, satellite)
  // Street mode respects theme (dark/light), satellite is always the same
  useEffect(() => {
    if (!map.current || !mapLoaded) return;
    const mapInstance = map.current;

    if (mapStyleMode === 'street') {
      // Use theme-appropriate style for 2D street view
      const streetStyle = isDarkMode ? MAP_STYLES.dark : MAP_STYLES.light;
      // Guard: Don't set style if component is unmounting (prevents Mapbox globalId error)
      if (!isMountedRef.current) return;
      try {
        mapInstance.setStyle(streetStyle);
      } catch (e) {
        console.warn('[MapView] setStyle error (street, component may be unmounting):', e);
        return;
      }
      // Wait for style to load, then reset pitch for 2D street view
      mapInstance.once('styledata', () => {
        // Guard: Check mounted, map exists, and style is ready
        if (!isMountedRef.current || !map.current) return;
        try {
          if (map.current.isStyleLoaded()) {
            map.current.easeTo({ pitch: 0, duration: 1000 });
          }
        } catch (e) {
          console.warn('[MapView] Style event handler error (street):', e);
        }
      });
    } else if (mapStyleMode === 'satellite') {
      // Guard: Don't set style if component is unmounting (prevents Mapbox globalId error)
      if (!isMountedRef.current) return;
      try {
        mapInstance.setStyle(MAP_STYLES.satellite);
      } catch (e) {
        console.warn('[MapView] setStyle error (satellite, component may be unmounting):', e);
        return;
      }
      // Wait for style to load, then reset pitch for 2D satellite view
      mapInstance.once('styledata', () => {
        // Guard: Check mounted, map exists, and style is ready
        if (!isMountedRef.current || !map.current) return;
        try {
          if (map.current.isStyleLoaded()) {
            map.current.easeTo({ pitch: 0, duration: 1000 });
          }
        } catch (e) {
          console.warn('[MapView] Style event handler error (satellite):', e);
        }
      });
    }
  }, [mapLoaded, mapStyleMode, isDarkMode]); // Street mode respects theme

  // === 3D MODE MANAGEMENT ===
  // Handle 3D mode style loading and lighting configuration
  // Only depends on is3DTimeDay - NOT isDarkMode directly (theme sync handles that)
  useEffect(() => {
    if (!map.current || !mapLoaded || mapStyleMode !== '3d') return;
    const mapInstance = map.current;

    // Determine the correct lighting preset based on current state
    const lightPreset = is3DTimeDay ? 'day' : 'night';
    console.log(`🌙 [3D Init] is3DTimeDay=${is3DTimeDay}, will load with lightPreset=${lightPreset}`);

    // Load Standard style when entering 3D mode
    // Only load if not already loaded to avoid unnecessary reloads
    // NOTE: We intentionally do NOT check isStyleLoaded() here because:
    // 1. We need to call setStyle() to change TO 3D mode
    // 2. isStyleLoaded() checks are correct INSIDE event callbacks (style.load, styledata)
    // 3. The try/catch around setStyle() handles any errors during transition
    // getStyleName() never throws — getStyle() itself throws "Style is not
    // done loading" mid-transition (Sentry HOLLYWOODTRUCKS-API-8).
    if (getStyleName(mapInstance) !== 'Mapbox Standard') {
      // Guard: Ensure component is still mounted before style change
      if (!isMountedRef.current) return;
      try {
        mapInstance.setStyle(MAP_STYLES.standard);
      } catch (e) {
        console.warn('[MapView] setStyle error (3D, component may be unmounting):', e);
        return;
      }

      // Apply config AS SOON AS the style is loaded to minimize flicker
      // Use 'style.load' which fires when style is ready to accept config
      mapInstance.once('style.load', () => {
        // Guard: Check mounted and map exists
        if (!isMountedRef.current || !map.current) return;

        try {
          map.current.setConfig('basemap', {
            lightPreset,
            showPointOfInterestLabels: false,
          });
          console.log(`✅ [3D Mode] Applied ${lightPreset} config on style.load (minimal flicker)`);
        } catch (e) {
          console.warn('[3D Mode] Failed to set config on style.load:', e);
        }
      });

      // Wait for style to load, then set 60-degree pitch for 3D buildings
      mapInstance.once('styledata', () => {
        // Guard: Check mounted, map exists, and style is ready
        if (!isMountedRef.current || !map.current) return;
        try {
          if (map.current.isStyleLoaded()) {
            map.current.easeTo({ pitch: 60, duration: 1000 });
          }
        } catch (e) {
          console.warn('[MapView] Style event handler error (3D):', e);
        }
      });
    } else {
      // Style already loaded, just update the config
      // This handles manual time toggle when already in 3D mode
      if (mapInstance.isStyleLoaded()) {
        try {
          mapInstance.setConfig('basemap', {
            lightPreset,
            showPointOfInterestLabels: false,
          });
          console.log(`✅ [3D Mode] Updated lighting to ${lightPreset}`);
        } catch (e) {
          console.warn('[3D Mode] Failed to set config:', e);
        }
      }
    }
  }, [mapLoaded, mapStyleMode, is3DTimeDay]); // Depends on is3DTimeDay, NOT isDarkMode

  // === TIME SWITCHER CONTROL VISIBILITY ===
  // Show time switcher control only in 3D mode
  useEffect(() => {
    if (!map.current || !mapLoaded || !timeSwitcherControlRef.current) return;

    const mapInstance = map.current;
    const control = timeSwitcherControlRef.current;

    // Remove control first (if it exists)
    try {
      mapInstance.removeControl(control);
    } catch (_e) {
      // Control wasn't added, ignore
    }

    // Only add control if we're in 3D mode
    if (mapStyleMode === '3d') {
      // Add time switcher control next to style switcher
      if (isMobile) {
        // Mobile: Add to bottom-right stack (above style switcher)
        mapInstance.addControl(control, 'bottom-right');
      } else {
        // Desktop: Add to bottom-left stack (below style switcher)
        mapInstance.addControl(control, 'bottom-left');
      }
    }

    return () => {
      // Clean up on unmount
      if (map.current && timeSwitcherControlRef.current) {
        try {
          map.current.removeControl(timeSwitcherControlRef.current);
        } catch (_e) {
          // Control wasn't added, ignore
        }
      }
    };
  }, [mapLoaded, mapStyleMode, isMobile]);

  // === UNIVERSAL STYLE CHANGE LISTENER ===
  // Increment styleLoaded for ANY style change (satellite/street, light/dark)
  // This ensures markers are restored after StyleSwitcherControl changes
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    const handleStyleData = () => {
      // Guard: Only process if component is still mounted
      if (!isMountedRef.current) return;
      // Style has loaded, increment counter to trigger layer re-addition
      setStyleLoaded((prev) => prev + 1);
    };

    // Listen for styledata event (fires when style is fully loaded)
    map.current.on('styledata', handleStyleData);

    return () => {
      if (map.current) {
        map.current.off('styledata', handleStyleData);
      }
    };
  }, [mapLoaded]);

  // === VIEWPORT TRACKING ===
  // Track viewport changes and sync to URL for shareable map views
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    const handleMoveEnd = () => {
      syncViewportToUrl();
      // Notify parent of viewport change for bbox filtering
      if (onViewportChange && map.current) {
        const bounds = map.current.getBounds();
        if (bounds) {
          onViewportChange(bounds);
        }
      }
    };

    map.current.on('moveend', handleMoveEnd);

    // Initial bounds notification
    if (onViewportChange && map.current) {
      const bounds = map.current.getBounds();
      if (bounds) {
        onViewportChange(bounds);
      }
    }

    return () => {
      map.current?.off('moveend', handleMoveEnd);
    };
  }, [mapLoaded, syncViewportToUrl, onViewportChange]);

  // === DATA SOURCES AND LAYERS ===

  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    let timeoutId: NodeJS.Timeout;

    const updateSources = () => {
      if (!map.current) return; // Guard: map might be unmounted during retry

      // 🛡️ CRITICAL FIX: Wait for style to be fully loaded before adding sources
      if (!map.current.isStyleLoaded()) {
        console.log('⏳ [MapView] Style not ready, retrying in 100ms...');
        timeoutId = setTimeout(updateSources, 100);
        return;
      }

      // === NOW SAFE TO PERFORM ALL SOURCE OPERATIONS ===

      // Dots source — unclustered, used for small dots at zoom 0-7
      const dotsSource = map.current.getSource(DOTS_SOURCE_ID) as mapboxgl.GeoJSONSource;
      if (dotsSource) {
        dotsSource.setData(geojson);
      } else {
        map.current.addSource(DOTS_SOURCE_ID, {
          type: 'geojson',
          data: geojson,
          cluster: false,
        });
      }

      // Clustered source — 50px radius, minPoints 4, zoom 9+
      const source = map.current.getSource(SOURCE_ID) as mapboxgl.GeoJSONSource;
      if (source) {
        source.setData(geojson);
      } else {
        map.current.addSource(SOURCE_ID, {
          type: 'geojson',
          data: geojson,
          cluster: clusterConfig.enabled,
          clusterMaxZoom: clusterConfig.maxZoom,
          clusterRadius: clusterConfig.radius,
          clusterMinPoints: clusterConfig.minPoints,
        });
      }

      // CACHE: Persist source data to sessionStorage for HMR resilience (dev only).
      // The restore path (mapStateCache.restoreLayerState at map init) only logs and
      // never re-applies this, so persisting the full GeoJSON in production is pure
      // cost — a multi-MB JSON.stringify on the main thread. Gate it to dev, where it
      // helps markers survive hot module reloads.
      if (import.meta.env.MODE === 'development') {
        saveSourceData(geojson);
      }

      // Signal that sources are ready for layer creation
      setSourcesReady(true);

      // Load unit icon after sources are added
      loadIcons();

      // Fit bounds on initial load only when there is no saved viewport (so refresh keeps zoom/location)
      const hasUrlViewport = searchParamsRef.current.get('zoom') || (searchParamsRef.current.get('lng') && searchParamsRef.current.get('lat'));
      const hasStoredViewport = getStoredViewport();
      const shouldFitBounds = units.length > 0 && !initialLoadDone.current && !hasUrlViewport && !hasStoredViewport;

      if (shouldFitBounds) {
        // Filter out units without valid GPS coordinates (not 0,0, not missing)
        const unitsWithLocation = units.filter(t => isValidLocation(t.location));

        if (unitsWithLocation.length > 0) {
          // Use weighted centroid bounds to center on where most units are
          // This prevents outliers (e.g., a few units in Europe) from distorting the view
          // when 99% of units are in the USA
          const coordinates = unitsWithLocation.map(u => ({ lat: u.location.lat, lng: u.location.lng }));
          const weightedBounds = calculateWeightedCentroidBounds(coordinates, 0.95);

          if (weightedBounds) {
            const paddingOptions = isMobile ? BOUNDS_PADDING.mobile : BOUNDS_PADDING.desktop;
            const symmetricBounds = new mapboxgl.LngLatBounds(
              weightedBounds.sw,
              weightedBounds.ne
            );

            map.current.fitBounds(symmetricBounds, {
              padding: paddingOptions,
              duration: 0, // Instant on initial load
              maxZoom: ZOOM_THRESHOLDS.MAX_AUTO_ZOOM,
            });
          } else {
            // Fallback to simple fitBounds if weighted calculation fails
            const bounds = new mapboxgl.LngLatBounds();
            unitsWithLocation.forEach((unit) => bounds.extend([unit.location.lng, unit.location.lat]));

            const paddingOptions = isMobile ? BOUNDS_PADDING.mobile : BOUNDS_PADDING.desktop;
            map.current.fitBounds(bounds, {
              padding: paddingOptions,
              duration: 0,
              maxZoom: ZOOM_THRESHOLDS.MAX_AUTO_ZOOM,
            });
          }
        } else {
          // All units missing location - use appropriate default view based on fleet distribution
          const defaultConfig = getDefaultMapConfig(units);
          map.current.flyTo({
            center: defaultConfig.center,
            zoom: defaultConfig.zoom,
            duration: 0,
          });
        }
      }
      if (units.length > 0 && !initialLoadDone.current) {
        initialLoadDone.current = true;
      }
    };

    updateSources();

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [
    units,
    mapLoaded,
    styleLoaded,
    isMobile,
    geojson,
    clusterConfig,
    loadIcons,
  ]);

  // === NOTIFY PARENT WHEN MAP IS FULLY RENDERED ===
  // Wait for sources to be ready AND the map to reach 'idle' state (all tiles rendered,
  // all animations complete) before telling the parent to dismiss the preloader.
  // This prevents the user from seeing empty/loading tile blocks after the preloader fades.
  useEffect(() => {
    if (!map.current || !mapLoaded || !sourcesReady || hasNotifiedParentRef.current) return;

    const mapInstance = map.current;

    const notifyParent = () => {
      if (!hasNotifiedParentRef.current) {
        hasNotifiedParentRef.current = true;
        console.log('✅ [MapView] Map fully rendered (idle) - notifying parent');
        onMapLoaded?.();
      }
    };

    // If the map is already fully loaded (all tiles rendered), notify immediately
    if (mapInstance.loaded()) {
      notifyParent();
      return;
    }

    // Otherwise, wait for the 'idle' event which fires when:
    // - All tiles for the current viewport are downloaded and rendered
    // - All fade/transition animations have completed
    // - No camera transitions are in progress
    mapInstance.once('idle', notifyParent);

    // Fallback: if idle never fires within 8 seconds (e.g., offline tiles fail),
    // notify parent anyway so the user isn't stuck on the loading screen forever
    const fallbackTimer = setTimeout(() => {
      if (!hasNotifiedParentRef.current) {
        console.warn('[MapView] Map idle not received within 8s, dismissing preloader anyway');
        notifyParent();
      }
    }, 8000);

    return () => {
      mapInstance.off('idle', notifyParent);
      clearTimeout(fallbackTimer);
    };
  }, [mapLoaded, sourcesReady]); // eslint-disable-line react-hooks/exhaustive-deps

  // === CLUSTER RESTORATION FROM URL ===
  useEffect(() => {
    if (!map.current || !mapLoaded || !initialClusterCoords || !sourcesReady) return;

    // Delay slightly to ensure map features are rendered
    const timer = setTimeout(() => {
      if (!map.current) return;

      const [lng, lat] = initialClusterCoords;
      const point = map.current.project([lng, lat]);

      // Query for cluster features at the coordinates
      // Safety check: verify layer exists before querying
      if (!isLayerReady(map.current, 'clusters')) {
        return;
      }

      const features = map.current.queryRenderedFeatures(point, {
        layers: ['clusters'],
      });

      if (features.length > 0) {
        const feature = features[0];
        const clusterId = feature.properties?.cluster_id;
        const source = map.current.getSource(SOURCE_ID) as mapboxgl.GeoJSONSource;

        if (source && clusterId) {
          source.getClusterLeaves(clusterId, Infinity, 0, (err, leaves) => {
            if (err || !leaves) return;

            const clusterUnits = leaves
              .map((leaf) => {
                try {
                  return JSON.parse(leaf.properties!.unitData);
                } catch {
                  return null;
                }
              })
              .filter((unit): unit is Unit => unit !== null && unit.id !== undefined);

            if (clusterUnits.length > 0) {
              console.log('📍 [MapView] Restored cluster from URL:', {
                coords: initialClusterCoords,
                units: clusterUnits.length,
              });
              
              // Open the popup (or drawer on mobile)
              if (isMobile && mobileDrawer) {
                const type = clusterUnits.length <= 5 ? 'sm_cluster_tooltip' : 'lg_cluster_tooltip';
                mobileDrawer.openDrawer({
                  type,
                  units: clusterUnits,
                  coordinates: [lng, lat],
                });
              } else {
                setHoveredCluster({
                  units: clusterUnits,
                  coordinates: [lng, lat],
                });
              }
            }
          });
        }
      }
    }, 500); // 500ms delay for render

    return () => clearTimeout(timer);
  }, [mapLoaded, initialClusterCoords, sourcesReady, isMobile, mobileDrawer]);

  // === MOBILE URL SYNC ===
  useEffect(() => {
    if (!isMobile || !mobileDrawer.drawerContent) {
      // Only clear if we are mobile and drawer just closed (content became null)
      if (isMobile && !mobileDrawer.drawerContent) {
        syncClusterToUrl(null);
      }
      return;
    }

    const { type } = mobileDrawer.drawerContent;
    if (type === 'sm_cluster_tooltip' || type === 'lg_cluster_tooltip') {
      const { coordinates } = mobileDrawer.drawerContent;
      syncClusterToUrl(coordinates);
    } else {
      // If showing individual unit or other content, clear cluster param
      syncClusterToUrl(null);
    }
  }, [isMobile, mobileDrawer.drawerContent, syncClusterToUrl]);

  // === HOVER LAYER UPDATES ===

  useEffect(() => {
    // Check if the hovered unit is visually clustered (hidden)
    // We do this synchronously using queryRenderedFeatures to avoid flash of unclustered highlight
    let isClustered = false;
    
    // CRITICAL: Check if map style is loaded before querying features to avoid "Style is not done loading" error
    if (map.current && hoveredUnit && mapLoaded && map.current.isStyleLoaded()) {
      // Safety check: verify layer exists before querying
      if (isLayerReady(map.current, 'clusters')) {
        const point = map.current.project([hoveredUnit.location.lng, hoveredUnit.location.lat]);
        const clusterFeatures = map.current.queryRenderedFeatures(point, {
          layers: ['clusters']
        });

        if (clusterFeatures.length > 0) {
          isClustered = true;
        }
      }
    }

    updateHoverLayer(hoveredUnit ?? null, currentZoom, isClustered);
  }, [hoveredUnit, currentZoom, updateHoverLayer, mapLoaded]);

  // === CLUSTER DETECTION FOR SIDEBAR HOVER ===
  // When hovering a unit from sidebar, check if it's part of a cluster and open the cluster popup
  useEffect(() => {
    if (!map.current || !mapLoaded || !hoveredUnit) {
      return;
    }

    // CRITICAL: If a cluster popup is already open and the hovered unit is in that cluster,
    // do NOT update the cluster state - this prevents the popup from jumping/repositioning
    // when hovering sidebar items that are already displayed in the cluster popup
    if (hoveredCluster && hoveredCluster.units.some(u => u.id === hoveredUnit.id)) {
      return;
    }

    const unitCoords: [number, number] = [hoveredUnit.location.lng, hoveredUnit.location.lat];
    const point = map.current.project(unitCoords);

    // CRITICAL: Check if map style is loaded before querying features to avoid "Style is not done loading" error
    if (!map.current.isStyleLoaded()) return;

    // Check clusters (zoom 9+)
    if (currentZoom >= ZOOM_THRESHOLDS.FULL_MARKERS_MIN) {
      // Check if layer exists before querying
      if (!isLayerReady(map.current, 'clusters')) {
        return;
      }

      const clusterFeatures = map.current.queryRenderedFeatures(point, {
        layers: ['clusters'],
      });

      if (clusterFeatures.length > 0) {
        const clusterId = clusterFeatures[0].properties?.cluster_id;
        const source = map.current.getSource(SOURCE_ID) as mapboxgl.GeoJSONSource;

        source.getClusterLeaves(clusterId, Infinity, 0, (err, leaves) => {
          if (err || !leaves) return;

          const clusterUnits = leaves
            .map((leaf) => {
              try {
                return JSON.parse(leaf.properties!.unitData);
              } catch {
                return null;
              }
            })
            .filter((unit): unit is Unit => unit !== null && unit.id !== undefined);

          if (clusterUnits.length > 0 && clusterUnits.some((u: Unit) => u.id === hoveredUnit.id)) {
            // Found the cluster containing this unit!
            setHoveredCluster({
              units: clusterUnits,
              coordinates: unitCoords,
            });
          } else {
            // Unit not in this cluster (shouldn't happen, but be defensive)
            setHoveredCluster(null);
          }
        });
        return; // Found cluster features, waiting for async result
      }
    }

    // No cluster features found at this point - unit is NOT clustered
    // Clear cluster state immediately so individual popup shows
    setHoveredCluster(null);
  }, [hoveredUnit, mapLoaded, currentZoom, hoveredCluster]);

  // === FIT BOUNDS WHEN SIDEBAR HOVER CHANGES ===
  // When hovering a unit from sidebar, pan/zoom map to show it if off-screen
  useEffect(() => {
    if (!map.current || !hoveredUnit) return;

    const unitCoords: [number, number] = [hoveredUnit.location.lng, hoveredUnit.location.lat];
    const paddingOptions = isMobile ? BOUNDS_PADDING.mobile : BOUNDS_PADDING.desktop;

    // Check if unit is effectively visible in the safe area
    // queryRenderedFeatures/contains only check if in viewport, not if occluded by sidebar
    const point = map.current.project(unitCoords);
    const canvas = map.current.getCanvas();
    
    const isVisible = 
      point.x >= paddingOptions.left &&
      point.x <= canvas.width - paddingOptions.right &&
      point.y >= paddingOptions.top &&
      point.y <= canvas.height - paddingOptions.bottom;

    if (!isVisible) {
      // Unit is off-screen or under sidebar - fly to it
      map.current.flyTo({
        center: unitCoords,
        zoom: Math.max(currentZoom, ZOOM_THRESHOLDS.SMALL_DOTS_MIN + 2), // At least zoom 4 to show marker clearly
        padding: paddingOptions,
        duration: 400, // Quick but smooth animation (0.4 seconds)
        essential: true, // Ensures animation completes even if interrupted
      });
    }
  }, [hoveredUnit, isMobile, currentZoom]);

  // Calculate sidebar offset for desktop control positioning
  // Right controls are 10px from edge (globals.css: .mapboxgl-ctrl-bottom-right { right: 10px !important; })
  // We want left controls to be same distance from sidebar: 60/360 + 10px
  const sidebarOffset = isSidebarCollapsed ? '110px' : '370px'; // 100px/360px width + 10px padding

  // === RENDER ===

  return (
    <div
      className="relative w-full h-full bg-background"
      style={{ '--sidebar-offset': isMobile ? '0px' : sidebarOffset } as React.CSSProperties}
    >
      {/* WebGL Not Supported Fallback */}
      {webGLSupported === false && (
        <div className="absolute inset-0 flex items-center justify-center bg-background">
          <div className="text-center max-w-md mx-auto p-8">
            <svg
              className="w-16 h-16 text-muted-foreground mx-auto mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
            <h2 className="text-lg font-semibold mb-2">3D Graphics Not Available</h2>
            <p className="text-muted-foreground text-sm mb-4">
              The interactive map requires WebGL support, which is not available in your current browser or environment.
            </p>
            <p className="text-muted-foreground text-sm">
              Please try:
            </p>
            <ul className="text-muted-foreground text-sm text-left mt-2 space-y-1">
              <li>• Using a modern browser (Chrome, Firefox, Safari, Edge)</li>
              <li>• Enabling hardware acceleration in your browser settings</li>
              <li>• Checking that WebGL is not disabled in your browser</li>
            </ul>
          </div>
        </div>
      )}

      {/* Map Container - Only render if WebGL is supported */}
      {webGLSupported !== false && (
        <div
          ref={mapContainer}
          className={isMobile ? 'absolute top-0 left-0 right-0 bottom-0' : 'absolute inset-0'}
        />
      )}

      {/* Loading is now handled by parent Map.tsx for unified experience */}

      {onViewportFilterChange && (
        <ViewportFilterButton
          enabled={viewportFilterEnabled}
          onChange={onViewportFilterChange}
          isSidebarCollapsed={isSidebarCollapsed}
          hasRestorableFilters={hasSavedFilters}
          onRestoreFilters={restoreSavedFilters}
        />
      )}

      {geolocationError && (
        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-30 bg-destructive text-destructive-foreground px-4 py-2 rounded-md shadow-lg max-w-md text-sm">
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span>{geolocationError}</span>
            <button
              onClick={() => setGeolocationError(null)}
              className="ml-2 hover:opacity-70"
              aria-label="Dismiss error"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      <style>{`
        /* Custom Attribution Strip */
        .mapboxgl-ctrl-bottom-left {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
        }

        .custom-map-attribution {
          margin-top: -4px;
          padding: 2px 6px;
          font-size: 10px;
          line-height: 1.1;
          color: rgba(255, 255, 255, 0.75);
          pointer-events: none;
        }
        
        [data-theme="dark"] .custom-map-attribution {
          color: rgba(0, 0, 0, 0.75);
        }

        .mapboxgl-popup {
          z-index: 30 !important;
        }
        ${
          selectedUnit
            ? `
          /* Desktop: detail panel overlays the map, so hide the bottom-right
             controls that would otherwise show behind it. On mobile the map
             is hidden via the parent tab container, and selecting a unit
             keeps state when returning to the Map tab — keep controls visible. */
          @media (min-width: 769px) {
            .mapboxgl-ctrl-top-right,
            .mapboxgl-ctrl-bottom-right {
              display: none !important;
            }
          }
        `
            : ''
        }
        .mapboxgl-ctrl-fullscreen .mapboxgl-ctrl-icon,
        .mapboxgl-ctrl-geolocate .mapboxgl-ctrl-icon,
        .mapboxgl-ctrl-compass .mapboxgl-ctrl-icon {
          width: 24px !important;
          height: 24px !important;
          background-size: 18px 18px !important;
          background-position: center center !important;
          background-repeat: no-repeat !important;
        }
        .mapboxgl-ctrl-fullscreen,
        .mapboxgl-ctrl-geolocate,
        .mapboxgl-ctrl-compass,
        .style-switcher-button,
        .reset-view-button {
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
        }
        .mapboxgl-ctrl-compass-icon svg {
          filter: ${isDarkMode ? 'invert(1)' : 'none'};
        }

        [data-theme="dark"] .mapboxgl-ctrl-geolocate .mapboxgl-ctrl-icon {
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='29' height='29' viewBox='0 0 29 29' fill='none' stroke='%2310b981' stroke-width='2'%3E%3Ccircle cx='14.5' cy='14.5' r='8'/%3E%3Cline x1='14.5' y1='2' x2='14.5' y2='9'/%3E%3Cline x1='14.5' y1='20' x2='14.5' y2='27'/%3E%3Cline x1='2' y1='14.5' x2='9' y2='14.5'/%3E%3Cline x1='20' y1='14.5' x2='27' y2='14.5'/%3E%3C/svg%3E") !important;
        }
        [data-theme="dark"] .mapboxgl-ctrl-geolocate.mapboxgl-ctrl-geolocate-active .mapboxgl-ctrl-icon {
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='29' height='29' viewBox='0 0 29 29' fill='none' stroke='%2310b981' stroke-width='2'%3E%3Ccircle cx='14.5' cy='14.5' r='8'/%3E%3Cline x1='14.5' y1='2' x2='14.5' y2='9'/%3E%3Cline x1='14.5' y1='20' x2='14.5' y2='27'/%3E%3Cline x1='2' y1='14.5' x2='9' y2='14.5'/%3E%3Cline x1='20' y1='14.5' x2='27' y2='14.5'/%3E%3Ccircle cx='14.5' cy='14.5' r='2' fill='%2310b981'/%3E%3C/svg%3E") !important;
        }

        [data-theme="light"] .mapboxgl-ctrl-geolocate .mapboxgl-ctrl-icon {
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='29' height='29' viewBox='0 0 29 29' fill='none' stroke='%2310b981' stroke-width='2'%3E%3Ccircle cx='14.5' cy='14.5' r='8'/%3E%3Cline x1='14.5' y1='2' x2='14.5' y2='9'/%3E%3Cline x1='14.5' y1='20' x2='14.5' y2='27'/%3E%3Cline x1='2' y1='14.5' x2='9' y2='14.5'/%3E%3Cline x1='20' y1='14.5' x2='27' y2='14.5'/%3E%3C/svg%3E") !important;
        }
        [data-theme="light"] .mapboxgl-ctrl-geolocate.mapboxgl-ctrl-geolocate-active .mapboxgl-ctrl-icon {
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='29' height='29' viewBox='0 0 29 29' fill='none' stroke='%2310b981' stroke-width='2'%3E%3Ccircle cx='14.5' cy='14.5' r='8'/%3E%3Cline x1='14.5' y1='2' x2='14.5' y2='9'/%3E%3Cline x1='14.5' y1='20' x2='14.5' y2='27'/%3E%3Cline x1='2' y1='14.5' x2='9' y2='14.5'/%3E%3Cline x1='20' y1='14.5' x2='27' y2='14.5'/%3E%3Ccircle cx='14.5' cy='14.5' r='2' fill='%2310b981'/%3E%3C/svg%3E") !important;
        }

        /* Desktop Sidebar Adjustment */
        @media (min-width: 769px) {
          .mapboxgl-ctrl-bottom-left,
          .mapboxgl-ctrl-top-left {
            left: var(--sidebar-offset) !important;
            transition: left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          }
        }
      `}</style>

      {/* Mobile Info Drawer - always render for consistent hook count (md:hidden handles desktop) */}
      <MobileInfoDrawer
        drawer={mobileDrawer}
        map={map.current}
        allUnits={units}
        updateHoverLayer={updateHoverLayer}
        currentZoom={currentZoom}
      />
    </div>
  );
}
